import React, { useState, useEffect, useRef } from 'react';
import { marked } from 'marked';
import { useAuth } from '../contexts/AuthContext';
import api from '../utils/api';

marked.setOptions({ breaks: true, gfm: true });

export default function AIChat({ onClose }) {
  const { user } = useAuth();
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [streaming, setStreaming] = useState(false);
  const [fullscreen, setFullscreen] = useState(() => localStorage.getItem('ai_chat_fullscreen') === '1');
  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);

  function toggleFullscreen() {
    setFullscreen(f => {
      const next = !f;
      localStorage.setItem('ai_chat_fullscreen', next ? '1' : '0');
      return next;
    });
  }

  if (!user || !user.permissions?.use_ai_chat) return null;

  useEffect(() => {
    api.get('/api/ai/history').then(({ data }) => {
      setMessages(data.messages.map(m => ({
        id: m.id, role: m.role, content: m.content, timestamp: m.created_at
      })));
    }).catch(() => {});
  }, []);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  async function handleSend(e) {
    e.preventDefault();
    const msg = input.trim();
    if (!msg || streaming) return;

    const userMsg = { id: Date.now(), role: 'user', content: msg, timestamp: new Date().toLocaleString('sv-SE', { timeZone: 'America/New_York' }).replace(' ', 'T') };
    setMessages(prev => [...prev, userMsg]);
    setInput('');
    setStreaming(true);

    const assistantId = Date.now() + 1;
    setMessages(prev => [...prev, { id: assistantId, role: 'assistant', content: '', timestamp: '', streaming: true }]);

    const controller = new AbortController();
    const clientTimeout = setTimeout(() => controller.abort(), 65000);

    try {
      const response = await fetch('/api/ai/chat', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ message: msg }),
        signal: controller.signal
      });

      const reader = response.body.getReader();
      const decoder = new TextDecoder();
      let fullText = '';

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;

        const chunk = decoder.decode(value);
        const lines = chunk.split('\n');

        for (const line of lines) {
          if (!line.startsWith('data: ')) continue;
          try {
            const data = JSON.parse(line.slice(6));
            if (data.text) {
              fullText += data.text;
              setMessages(prev => prev.map(m =>
                m.id === assistantId ? { ...m, content: fullText, streaming: true } : m
              ));
            }
            if (data.done) {
              setMessages(prev => prev.map(m =>
                m.id === assistantId ? { ...m, streaming: false, timestamp: new Date().toLocaleString('sv-SE', { timeZone: 'America/New_York' }).replace(' ', 'T') } : m
              ));
            }
            if (data.error) {
              setMessages(prev => prev.map(m =>
                m.id === assistantId ? { ...m, content: data.error, streaming: false } : m
              ));
            }
          } catch {}
        }
      }
    } catch (err) {
      const errorMsg = err.name === 'AbortError'
        ? 'Response timed out. Please try again.'
        : 'Failed to connect. Please try again.';
      setMessages(prev => prev.map(m =>
        m.id === assistantId ? { ...m, content: errorMsg, streaming: false } : m
      ));
    } finally {
      clearTimeout(clientTimeout);
      // Always clear streaming state on the assistant message — handles cases where
      // the SSE stream ends without sending a done/error signal
      setMessages(prev => prev.map(m =>
        m.id === assistantId && m.streaming
          ? { ...m, streaming: false, content: m.content || 'Something went wrong. Please try again.' }
          : m
      ));
    }

    setStreaming(false);
    inputRef.current?.focus();
  }

  function formatTime(ts) {
    if (!ts) return '';
    try {
      const d = new Date(ts + (ts.includes('T') && !ts.includes('Z') ? '' : ''));
      return d.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', hour12: true, timeZone: 'America/New_York' });
    } catch { return ''; }
  }

  return (
    <div className={`ai-chat-window${fullscreen ? ' ai-chat-window-fullscreen' : ''}`}>
      <div className="ai-chat-header">
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          <img src="/nucleusai.png" alt="" style={{ height: '22px' }} />
          <span style={{ fontWeight: 600, fontSize: '14px' }}>Ask Nucleus AI</span>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
          <button onClick={toggleFullscreen} className="ai-chat-minimize"
                  title={fullscreen ? 'Exit fullscreen' : 'Fullscreen'}
                  aria-label={fullscreen ? 'Exit fullscreen' : 'Enter fullscreen'}>
            {fullscreen ? (
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <polyline points="4 14 10 14 10 20" />
                <polyline points="20 10 14 10 14 4" />
                <line x1="14" y1="10" x2="21" y2="3" />
                <line x1="3" y1="21" x2="10" y2="14" />
              </svg>
            ) : (
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <polyline points="15 3 21 3 21 9" />
                <polyline points="9 21 3 21 3 15" />
                <line x1="21" y1="3" x2="14" y2="10" />
                <line x1="3" y1="21" x2="10" y2="14" />
              </svg>
            )}
          </button>
          <button onClick={onClose} className="ai-chat-minimize" title="Minimize" aria-label="Minimize chat">&minus;</button>
        </div>
      </div>

      <div className="ai-chat-messages">
        {messages.length === 0 && (
          <div style={{ textAlign: 'center', color: 'var(--text-light)', padding: '40px 20px', fontSize: '13px' }}>
            Ask me anything about your business data — today's sales, trends, break performance, and more.
          </div>
        )}
        {messages.map(m => (
          <div key={m.id} className={`ai-msg ${m.role}`}>
            <div className="ai-msg-label">{m.role === 'user' ? 'You' : 'Nucleus AI'}</div>
            <div className="ai-msg-content">
              {m.content
                ? (m.role === 'assistant'
                  ? <div dangerouslySetInnerHTML={{ __html: marked.parse(m.content) }} />
                  : m.content)
                : (m.streaming ? <span className="ai-typing"><span /><span /><span /></span> : '')}
            </div>
            {m.timestamp && <div className="ai-msg-time">{formatTime(m.timestamp)}</div>}
          </div>
        ))}
        <div ref={messagesEndRef} />
      </div>

      <form className="ai-chat-input" onSubmit={handleSend}>
        <input
          ref={inputRef}
          type="text"
          placeholder="Ask about your business..."
          value={input}
          onChange={e => setInput(e.target.value)}
          disabled={streaming}
          autoFocus
        />
        <button type="submit" disabled={streaming || !input.trim()} className="ai-send-btn">
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <line x1="22" y1="2" x2="11" y2="13" /><polygon points="22 2 15 22 11 13 2 9 22 2" />
          </svg>
        </button>
      </form>
    </div>
  );
}
